import type { Implementation } from "@pdfking/functionpool"
import type {
  TaskImplementations,
  TaskMap,
  TrackingCallback,
} from "../types/TaskMap"
import type {
  TaskOptions,
  WireInput,
  WireOutput,
  WireRequest,
  WireResponse,
} from "../types/WireTypes"
import { Channel, First, MapAsync, ProcessIterable } from "@pdfking/tfunc"
import { EngineMaker } from "./EngineMaker"

export const ReceiverMaker = <TM extends TaskMap>(
  tasks: TaskImplementations<TM>
): Implementation<WireRequest<TM>, WireResponse> =>
  async function* (input, forward) {
    const {
      enum: task_name,
      task_id,
      ...props
    } = (await First(input)) as TaskOptions<TM>

    const { push, stop, stream } = Channel<WireResponse>()

    const emit: TrackingCallback = event =>
      push({ enum: "tracking", task_id, ...event })

    const context = {
      emit,
      engine: EngineMaker<TM>(() => forward),
      task_id,
      ...props,
    }

    const handler = tasks[task_name as keyof TM & string]

    const unwrappedInput = MapAsync(
      msg => (msg as WireInput).value,
      input
    ) as AsyncGenerator<TM[keyof TM & string]["input"]>

    void ProcessIterable(
      MapAsync(
        value => ({ enum: "output", value }) as WireOutput,
        handler(context as never, unwrappedInput)
      ),
      push,
      stop
    )

    yield* stream
  }
